import { Component } from '@theme/component';
import { API_ENDPOINTS } from '@theme/api-endpoints';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} notice - The notice element, rendered `hidden` server-side.
 */

/** @type {Promise<object | null> | null} */
let geoInfoPromise = null;

/**
 * Fetch the geo-info endpoint once and share the result across all notice
 * instances (a cart can hold several). Any failure resolves to null — the
 * warning simply stays hidden.
 *
 * @returns {Promise<object | null>}
 */
function getGeoInfo() {
  if (!geoInfoPromise) {
    geoInfoPromise = fetch(API_ENDPOINTS.cfGeoInfo)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }
  return geoInfoPromise;
}

/**
 * Prop 65 Notice — geo-conditional visibility.
 *
 * The notice is rendered server-side with its content `hidden`. On connect the
 * component fetches the shared geo-info result and reveals the notice when
 * `show_prop65_warning` is true and the region is California.
 *
 * @extends {Component<Refs>}
 */
class Prop65Notice extends Component {
  requiredRefs = ['notice'];

  /** @type {MutationObserver | null} */
  #hiddenObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#hiddenObserver?.disconnect();
    this.#hiddenObserver = null;
  }

  #reveal() {
    this.refs.notice?.removeAttribute('hidden');
  }

  async #init() {
    const data = await getGeoInfo();
    const showWarning = data?.show_prop65_warning === true && data?.region === 'California';
    if (!showWarning) return;
    this.#reveal();

    const { notice } = this.refs;
    if (!notice) return;

    // Cart section morphs reuse this element and re-copy the server-rendered
    // `hidden` attribute onto the notice without re-running connectedCallback;
    // watch that one attribute and remove it again.
    this.#hiddenObserver = new MutationObserver(() => {
      if (notice.hidden) this.#reveal();
    });
    this.#hiddenObserver.observe(notice, {
      attributes: true,
      attributeFilter: ['hidden'],
    });
  }
}

if (!customElements.get('prop65-notice')) {
  customElements.define('prop65-notice', Prop65Notice);
}
